import cx from "classnames";
import { BaseSyntheticEvent, useState } from "react";
import { Doodle } from "./Doodle";
import styles from "./Question.module.css";
import { AnswerOption, Question, Questions } from "./wizard.types";

const firstCharToUppercase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getScaleLabel = (value: number): string => {
  switch (value) {
    case 2:
      return "Helemaal mee eens";
    case 1:
      return "Mee eens";
    case 0:
      return "Neutraal";
    case -1:
      return "Niet mee eens";
    case -2:
      return "Helemaal niet mee eens";
    default:
      return "";
  }
};

export const QuestionComponent = ({
  questions,
  data,
  currentStep,
  setStep,
  goBack,
  number,
}: {
  questions: Questions;
  data: any;
  currentStep: keyof Questions;
  setStep: (value: any) => void;
  goBack?: (value: any) => void;
  number: number;
}) => {
  const [answer, setAnswer] = useState<any>(data[currentStep]);
  const { type, title, description, options, altOption, reverse } = questions[
    currentStep
  ] as Question;
  const multipleAnswersText = "Je kunt meerdere antwoorden selecteren.";

  const scaleOptions = Array(5)
    .fill(null)
    .map((_o: AnswerOption, index: number) => {
      const value = reverse ? 2 - index : -2 + index;
      return {
        label: getScaleLabel(value),
        value,
      };
    });

  // For checkbox
  const check = (e: BaseSyntheticEvent) => {
    const { value, checked } = e.target;

    if (checked === true) {
      setAnswer((prev: any) => {
        return Array.isArray(prev)
          ? [...prev.filter((v: string) => v !== altOption?.value), value]
          : [value];
      });
    } else {
      setAnswer((prev: any) => {
        return Array.isArray(prev)
          ? prev.filter((v: string) => v !== value && v !== altOption?.value)
          : [];
      });
    }
  };

  const checkAlt = (e: BaseSyntheticEvent) => {
    const { value, checked } = e.target;

    if (checked === true) {
      setAnswer(() => [value]);
    } else {
      setAnswer(() => []);
    }
  };

  const isChecked = (value: string | number) => {
    return Array.isArray(answer)
      ? answer.some((v) => v.toString() === value.toString())
      : value.toString() === answer?.toString();
  };

  const submit = (value: any) => {
    setTimeout(() => setStep(value === null ? answer : value), 500);
  };

  return (
    <div className={styles.wizardQuestion}>
      <div className={styles.header}>
        <Doodle />
        <h2>{title}</h2>
        {type === "scale" ? <p>Past deze uitspraak bij jou?</p> : null}
        {type === "checkbox"
          ? multipleAnswersText && <p>{multipleAnswersText}</p>
          : description && <p>{description}</p>}
      </div>
      <div className={styles.body}>
        <form
          className={styles.form}
          id={currentStep}
          onChange={(value) =>
            type !== "checkbox" &&
            setAnswer(() => {
              if (type === "scale") {
                return parseInt(
                  (value as BaseSyntheticEvent).target?.value,
                  10
                );
              }
              return (value as BaseSyntheticEvent).target?.value;
            })
          }
        >
          {(type === "checkbox" || type === "radio") && (
            <>
              {options &&
                options.map((option: AnswerOption, index: number) => {
                  return (
                    <div
                      onClick={(e) =>
                        type === "radio" ? submit(option.value) : {}
                      }
                      className={cx(
                        styles.option,
                        styles[options.length < 6 ? "noWrap" : ""]
                      )}
                      key={index}
                    >
                      <input
                        className={styles[`option-${type}`]}
                        name={type}
                        type={type}
                        id={type + index}
                        value={option.value}
                        checked={isChecked(option.value)}
                        onChange={(e) => (type === "checkbox" ? check(e) : {})}
                      />
                      <label
                        className={styles[`option-${type}__label`]}
                        htmlFor={type + index}
                      >
                        {firstCharToUppercase(option.label)}
                        {option.description && (
                          <span className={styles.description}>
                            {option.description}
                          </span>
                        )}
                      </label>
                    </div>
                  );
                })}
              {altOption && (
                <div
                  className={cx(
                    styles.option,
                    styles[options && options?.length < 6 ? "noWrap" : ""]
                  )}
                >
                  <input
                    className={styles[`option-${type}`]}
                    name={type}
                    type={type}
                    id={type + altOption.value}
                    value={altOption.value}
                    checked={isChecked(altOption.value)}
                    onChange={(e) => type === "checkbox" && checkAlt(e)}
                  />
                  <label
                    className={styles[`option-${type}__label`]}
                    htmlFor={type + altOption.value}
                  >
                    {altOption.label}
                    {altOption.description && (
                      <span className={styles.description}>
                        {altOption.description}
                      </span>
                    )}
                  </label>
                </div>
              )}
            </>
          )}
          {type === "scale" &&
            scaleOptions.map((option: AnswerOption, index: number) => {
              return (
                <div
                  className={styles.option}
                  key={index}
                  onClick={() => submit(option.value)}
                >
                  <input
                    className={styles[`option-radio`]}
                    name={type}
                    type="radio"
                    id={type + index}
                    value={option.value}
                    checked={isChecked(option.value)}
                    onChange={() => { }}
                  />
                  <label
                    className={styles[`option-radio__label`]}
                    htmlFor={type + index}
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
          {type === "none" && null}
        </form>
        <div className={styles.footer}>
          {goBack && <button onClick={() => goBack(answer)}>Terug</button>}
          {type !== "radio" && type !== "scale" && (
            <button
              style={{
                display:
                  (type !== "none" && answer === undefined) ||
                    answer === null ||
                    (Array.isArray(answer) && !answer?.length)
                    ? "none"
                    : "flex",
              }}
              onClick={() => submit(null)}
            >
              Verder
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
